import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { useDraws } from '../../../providers/DrawProvider';
import { usePurchase } from '../../../providers/PurchaseProvider';
import { checkoutGate } from '../utils/checkoutGate';
import { withFramingChoice } from '../utils/framingQuery';
import { resolveCheckoutIntentKind } from '../utils/intentKind';
import { hasPurchaseOptions } from '../utils/purchaseOptions';
import { resolveReleaseType } from '../utils/releaseType';

const productPageSlug = (
  slugQuery: string | string[] | undefined,
): string | null => {
  if (!slugQuery) {
    return null;
  }

  return Array.isArray(slugQuery) ? slugQuery.join('/') : slugQuery;
};

export const useProductCheckoutLink = (): string | null => {
  const { query } = useRouter();
  const { isAccountInDraw, mainDraw } = useDraws();
  const {
    backendProduct,
    frame,
    isFrameFromQuery,
    product: purchaseDetails,
  } = usePurchase();

  const product = mainDraw?.product ?? backendProduct;
  const slug = productPageSlug(query.slug) ?? mainDraw?.product.slug;
  const isEnteredSingleDraw = mainDraw
    ? resolveCheckoutIntentKind(mainDraw.draw) === 'single' &&
      isAccountInDraw(mainDraw.draw.id)
    : false;

  return useMemo(() => {
    if (!product || !slug) {
      return null;
    }

    if (!checkoutGate({ draw: mainDraw?.draw, product })) {
      return null;
    }

    const { isBuy, isDraw } = resolveReleaseType(product.releaseType);

    if (isDraw && isEnteredSingleDraw) {
      return null;
    }

    if (isBuy && !(purchaseDetails && hasPurchaseOptions(purchaseDetails))) {
      return null;
    }

    const link = `/checkout/${slug}`;

    // A frame picked on the page stays there; only one the visitor arrived
    // with in the URL is carried into checkout.
    return isFrameFromQuery ? withFramingChoice(link, frame) : link;
  }, [
    frame,
    isEnteredSingleDraw,
    isFrameFromQuery,
    mainDraw,
    product,
    purchaseDetails,
    slug,
  ]);
};
